
import React, { useEffect, useMemo, useState } from "react";
import { getEntitlementHistory } from "../../services/entitlements.service";
import apiServiceCompany from "../../services/company.services";
import { useCompanyId } from "../../services/CompanyidProvider";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

const T = {
  teal: "#188C8E",
  tealDark: "#0F6E70",
  tealLight: "#D6F3F3",
  tealExtraLight: "#E8F8F8",
  amber: "#F59E0B",
  border: "#E2E8F0",
};

const FIELD_CANDIDATES = {
  timestamp: [
    "modifiedAt",
    "updatedAt",
    "createdAt",
    "timestamp",
    "date",
    "time",
    "occurredAt",
    "modified_at",
    "updated_at",
    "created_at",
  ],
  actor: [
    "modifiedByEmail",
    "modifiedByName",
    "modifiedBy",
    "updatedByEmail",
    "updatedByName",
    "updatedBy",
    "createdByEmail",
    "createdByName",
    "createdBy",
    "changedBy",
    "changedByName",
    "performedBy",
    "performedByName",
    "actor",
    "actorName",
    "user",
    "userName",
    "fullName",
    "displayName",
    "username",
    "email",
    "userEmail",
  ],
  type: [
    "historyType",
    "type",
    "category",
    "changeType",
    "eventType",
    "kind",
  ],
  action: [
    "action",
    "operation",
    "changeAction",
    "eventAction",
    "verb",
  ],
  subject: [
    "entitlementName",
    "name",
    "resourceName",
    "itemName",
    "entityName",
    "title",
    "label",
  ],
  oldValue: ["oldValue", "previousValue", "before", "from", "old", "oldEntitlements"],
  newValue: ["newValue", "currentValue", "after", "to", "new", "value", "newEntitlements"],
  details: ["details", "description", "message", "summary", "reason"],
};

const USER_NAME_KEYS = [
  "name",
  "fullName",
  "displayName",
  "username",
  "userName",
  "email",
  "userEmail",
  "firstName",
  "lastName",
  "first_name",
  "last_name",
];

const normalizeText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const isNotSetValue = (value) => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    return trimmed === "" || trimmed === "notset" || trimmed === "not set";
  }

  return false;
};

const getChangeValueText = (value) => {
  if (isNotSetValue(value)) {
    return "Not Set";
  }

  return normalizeText(value);
};

const pickFirstDefined = (row, candidates) => {
  if (!row || typeof row !== "object") {
    return undefined;
  }

  for (const candidate of candidates) {
    if (Object.prototype.hasOwnProperty.call(row, candidate)) {
      const value = row[candidate];

      if (value !== null && value !== undefined && value !== "") {
        return value;
      }
    }
  }

  return undefined;
};

const getRowFieldText = (row, candidates) => {
  const value = pickFirstDefined(row, candidates);

  if (value === undefined || value === null || value === "") {
    return "-";
  }

  return normalizeText(value);
};

const resolveNameFromObject = (value) => {
  if (!value || typeof value !== "object") {
    return "";
  }

  const directValue = pickFirstDefined(value, USER_NAME_KEYS);

  if (directValue !== undefined && directValue !== null && directValue !== "") {
    if (typeof directValue === "object") {
      return resolveNameFromObject(directValue);
    }

    return normalizeText(directValue);
  }

  const firstName = pickFirstDefined(value, ["firstName", "first_name"]);
  const lastName = pickFirstDefined(value, ["lastName", "last_name"]);

  if (firstName || lastName) {
    return `${normalizeText(firstName || "")}${firstName && lastName ? " " : ""}${normalizeText(lastName || "")}`.trim();
  }

  return "";
};

const deepFindFirstByKeys = (source, keys, depth = 0, seen = new WeakSet()) => {
  if (!source || typeof source !== "object" || depth > 5) {
    return undefined;
  }

  if (seen.has(source)) {
    return undefined;
  }

  seen.add(source);

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const value = source[key];

      if (value !== null && value !== undefined && value !== "") {
        return value;
      }
    }
  }

  const values = Array.isArray(source) ? source : Object.values(source);

  for (const value of values) {
    const nestedFound = deepFindFirstByKeys(value, keys, depth + 1, seen);

    if (nestedFound !== undefined) {
      return nestedFound;
    }
  }

  return undefined;
};

const getActorText = (row) => {
  const directActorValue = pickFirstDefined(row, FIELD_CANDIDATES.actor);

  if (directActorValue !== undefined && directActorValue !== null && directActorValue !== "") {
    if (typeof directActorValue === "object") {
      const resolved = resolveNameFromObject(directActorValue);
      if (resolved) {
        return resolved;
      }
    } else {
      return normalizeText(directActorValue);
    }
  }

  const nestedActorValue = deepFindFirstByKeys(row, [...FIELD_CANDIDATES.actor, ...USER_NAME_KEYS]);

  if (nestedActorValue !== undefined && nestedActorValue !== null && nestedActorValue !== "") {
    if (typeof nestedActorValue === "object") {
      const resolved = resolveNameFromObject(nestedActorValue);
      return resolved || "-";
    }

    return normalizeText(nestedActorValue);
  }

  return "-";
};
const formatDateValue = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return normalizeText(value);
  }

  return date.toUTCString();
};

const getRowKey = (row, index) => {
  return (
    row?.id ||
    row?._id ||
    row?.historyId ||
    row?.logId ||
    row?.uuid ||
    `${index}-${normalizeText(pickFirstDefined(row, FIELD_CANDIDATES.timestamp))}-${normalizeText(
      pickFirstDefined(row, FIELD_CANDIDATES.subject)
    )}`
  );
};

const getHistoryBucket = (row) => {
  const rawType = getRowFieldText(row, FIELD_CANDIDATES.type);

  if (rawType !== "-") {
    return rawType.toUpperCase();
  }

  const hasEntitlementDiff =
    row?.oldEntitlements &&
    row?.newEntitlements &&
    typeof row.oldEntitlements === "object" &&
    typeof row.newEntitlements === "object";

  if (hasEntitlementDiff) {
    return "ENTITLEMENT";
  }

  const overridePairs = [
    ["oldOverrides", "newOverrides"],
    ["oldOverride", "newOverride"],
    ["oldCompanyOverrides", "newCompanyOverrides"],
    ["previousOverrides", "currentOverrides"],
  ];

  const hasOverrideDiff = overridePairs.some(([oldKey, newKey]) => {
    const oldObj = row?.[oldKey];
    const newObj = row?.[newKey];

    return oldObj && newObj && typeof oldObj === "object" && typeof newObj === "object";
  });

  if (hasOverrideDiff) {
    return "OVERRIDE";
  }

  const action = getRowFieldText(row, FIELD_CANDIDATES.action).toUpperCase();

  if (action.includes("OVERRIDE")) {
    return "OVERRIDE";
  }

  if (action.match(/ADD|CREATE|INSERT|UPDATE|EDIT|MODIFY|CHANGE|REMOVE|DELETE|ARCHIVE/)) {
    return "ENTITLEMENT";
  }

  if (JSON.stringify(row).toLowerCase().includes("override")) {
    return "OVERRIDE";
  }

  return "UNKNOWN";
};

const getChipColor = (bucket) => {
  if (bucket === "OVERRIDE") return "warning";
  if (bucket === "ENTITLEMENT") return "primary";
  if (bucket === "UNKNOWN") return "default";

  return "secondary";
};

const buildSearchText = (row) => {
  if (!row || typeof row !== "object") {
    return "";
  }

  return Object.entries(row)
    .map(([key, value]) => `${key} ${normalizeText(value)}`)
    .join(" ")
    .toLowerCase();
};

const getEntitlementChanges = (row) => {
  if (
    !row?.oldEntitlements ||
    !row?.newEntitlements ||
    typeof row.oldEntitlements !== "object" ||
    typeof row.newEntitlements !== "object"
  ) {
    return [];
  }

  const keys = new Set([...Object.keys(row.oldEntitlements), ...Object.keys(row.newEntitlements)]);
  const changes = [];

  for (const key of keys) {
    const oldVal = row.oldEntitlements[key];
    const newVal = row.newEntitlements[key];

    if (oldVal !== newVal) {
      changes.push({
        key,
        oldValue: oldVal,
        newValue: newVal,
      });
    }
  }

  return changes;
};

const getOverrideChanges = (row) => {
  const overridePairs = [
    ["oldOverrides", "newOverrides"],
    ["oldOverride", "newOverride"],
    ["oldCompanyOverrides", "newCompanyOverrides"],
    ["previousOverrides", "currentOverrides"],
  ];

  for (const [oldKey, newKey] of overridePairs) {
    const oldObj = row?.[oldKey];
    const newObj = row?.[newKey];

    if (!oldObj || !newObj || typeof oldObj !== "object" || typeof newObj !== "object") {
      continue;
    }

    const keys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
    const changes = [];

    for (const key of keys) {
      const oldVal = oldObj[key];
      const newVal = newObj[key];

      if (oldVal !== newVal) {
        changes.push({ key, oldValue: oldVal, newValue: newVal });
      }
    }

    if (changes.length > 0) {
      return changes;
    }
  }

  return [];
};

const buildEntitlementChangeSearchText = (row) => {
  const entitlementChanges = getEntitlementChanges(row);

  if (entitlementChanges.length === 0) {
    return "";
  }

  return entitlementChanges
    .map((change) => `${change.key} ${normalizeText(change.oldValue)} ${normalizeText(change.newValue)}`)
    .join(" ")
    .toLowerCase();
};

const buildOverrideChangeSearchText = (row) => {
  const overrideChanges = getOverrideChanges(row);

  if (overrideChanges.length === 0) {
    return "";
  }

  return overrideChanges
    .map((change) => `${change.key} ${normalizeText(change.oldValue)} ${normalizeText(change.newValue)}`)
    .join(" ")
    .toLowerCase();
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderHighlightedText = (text, query) => {
  const safeText = normalizeText(text);
  const trimmedQuery = query.trim();

  if (!safeText || !trimmedQuery) {
    return safeText;
  }

  const regex = new RegExp(`(${escapeRegExp(trimmedQuery)})`, "ig");
  const parts = safeText.split(regex);

  return parts.map((part, index) =>
    part.toLowerCase() === trimmedQuery.toLowerCase() ? (
      <Box
        component="mark"
        key={`${part}-${index}`}
        sx={{
          backgroundColor: "#fff3a3",
          color: "inherit",
          px: 0.2,
          borderRadius: "2px",
        }}
      >
        {part}
      </Box>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
};

const buildChangeSummary = (row) => {
  const subject = getRowFieldText(row, FIELD_CANDIDATES.subject);
  const details = getRowFieldText(row, FIELD_CANDIDATES.details);
  const oldValue = pickFirstDefined(row, FIELD_CANDIDATES.oldValue);
  const newValue = pickFirstDefined(row, FIELD_CANDIDATES.newValue);
  const action = getRowFieldText(row, FIELD_CANDIDATES.action).toUpperCase();

  const parts = [];

  const entitlementChanges = getEntitlementChanges(row);

  if (entitlementChanges.length > 0) {
    const firstChange = entitlementChanges[0];
    parts.push(`${firstChange.key}: ${normalizeText(firstChange.oldValue)} → ${normalizeText(firstChange.newValue)}`);
    if (entitlementChanges.length > 1) {
      parts.push(`+${entitlementChanges.length - 1} more`);
    }
    return parts.join(" | ");
  }

  if (subject !== "-") {
    parts.push(subject);
  }

  if (action !== "-") {
    parts.push(action);
  }

  if (oldValue !== undefined || newValue !== undefined) {
    parts.push(`${normalizeText(oldValue ?? "-")} → ${normalizeText(newValue ?? "-")}`);
  }

  if (details !== "-") {
    parts.push(details);
  }

  const nestedChange = row?.changes || row?.change || row?.diff || row?.meta?.changes;

  if (parts.length === 0 && nestedChange && typeof nestedChange === "object") {
    return Object.entries(nestedChange)
      .map(([key, value]) => `${key}: ${normalizeText(value)}`)
      .join(" | ");
  }

  return parts.length > 0 ? parts.join(" | ") : "-";
};

const getEntityFromEntitlements = (row) => {
  const entitlementChanges = getEntitlementChanges(row);

  if (entitlementChanges.length > 0) {
    return entitlementChanges[0].key;
  }

  return "-";
};

const normalizeHistoryRow = (row, index) => {
  const timestamp = pickFirstDefined(row, FIELD_CANDIDATES.timestamp);
  const entitlementSearchText = buildEntitlementChangeSearchText(row);
  const overrideSearchText = buildOverrideChangeSearchText(row);
  const baseSearchText = buildSearchText(row);

  return {
    ...row,
    __rowKey: getRowKey(row, index),
    __timestamp: timestamp,
    __timestampText: formatDateValue(timestamp),
    __actorText: getActorText(row),
    __typeText: getRowFieldText(row, FIELD_CANDIDATES.type),
    __actionText: getRowFieldText(row, FIELD_CANDIDATES.action),
    __subjectText: getRowFieldText(row, FIELD_CANDIDATES.subject),
    __entityText: getEntityFromEntitlements(row),
    __historyBucket: getHistoryBucket(row),
    __searchText: `${baseSearchText} ${entitlementSearchText} ${overrideSearchText}`.trim(),
    __summary: buildChangeSummary(row),
  };
};

export default function EntitlementHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, setCompanyId } = useCompanyId();

  const stateCompanyId = location?.state?.companyId || "";
  const stateCompanyName = location?.state?.companyName || "";
  const stateHistoryType = String(location?.state?.historyType || "ALL").toUpperCase();
  const initialHistoryType = ["ALL", "ENTITLEMENT", "OVERRIDE"].includes(stateHistoryType)
    ? stateHistoryType
    : "ALL";
  const effectiveCompanyId = stateCompanyId || companyId || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [historyType, setHistoryType] = useState(initialHistoryType);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [historyRows, setHistoryRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [dateFilterOption, setDateFilterOption] = useState("ALL");
  const [expandedRows, setExpandedRows] = useState({});
  const normalizedSearch = search.trim().toLowerCase();

  const handleBack = () => {
    navigate("/company-management", {
      state: {
        ...(location.state || {}),
        companyId: effectiveCompanyId,
        companyName: stateCompanyName,
        preserveState: true,
      },
    });
  };

  useEffect(() => {
    if (stateCompanyId && stateCompanyId !== companyId) {
      setCompanyId(stateCompanyId);
    }
  }, [stateCompanyId, companyId, setCompanyId]);

  useEffect(() => {
    setHistoryType(initialHistoryType);
  }, [initialHistoryType]);

  const calculateDateRange = (option) => {
    const today = new Date();
    let start = "";
    let end = "";

    switch (option) {
      case "LAST_MODIFICATION": {
        const last7Days = new Date(today);
        last7Days.setDate(today.getDate() - 7);
        start = last7Days.toISOString().split("T")[0];
        end = today.toISOString().split("T")[0];
        break;
      }

      case "THIS_MONTH":
        start = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
        end = today.toISOString().split("T")[0];
        break;

      case "THIS_YEAR":
        start = `${today.getFullYear()}-01-01`;
        end = today.toISOString().split("T")[0];
        break;

      case "LAST_YEAR": {
        const lastYear = today.getFullYear() - 1;
        start = `${lastYear}-01-01`;
        end = `${lastYear}-12-31`;
        break;
      }

      default:
        return { start: "", end: "" };
    }

    return { start, end };
  };

  useEffect(() => {
    let isActive = true;

    const loadHistory = async () => {
      if (!effectiveCompanyId) {
        setLoading(false);
        setError("No company selected");
        setHistoryRows([]);
        setTotalCount(0);
        setTotalPages(0);
        return;
      }

      setLoading(true);
      setError("");

      console.log("companyId:", effectiveCompanyId);

      try {
        const [entitlementResponse, overrideHistoryResponse] = await Promise.all([
          getEntitlementHistory(effectiveCompanyId, page + 1),
          apiServiceCompany.getCompanyOverridesHistory(effectiveCompanyId, page + 1, 25).catch(() => ({
            status: "ok",
            data: { values: [], totalCount: 0, totalPages: 0 },
          })),
        ]);

        console.log("Entitlement history API response:", entitlementResponse);
        console.log("Override history API response:", overrideHistoryResponse);

        const entitlementRows = Array.isArray(entitlementResponse?.data?.values)
          ? entitlementResponse.data.values
          : [];
        const overrideRows = Array.isArray(overrideHistoryResponse?.data?.values)
          ? overrideHistoryResponse.data.values
          : [];

        const mergedHistoryData = [...entitlementRows, ...overrideRows].sort((a, b) => {
          const aTime = new Date(pickFirstDefined(a, FIELD_CANDIDATES.timestamp) || 0).getTime();
          const bTime = new Date(pickFirstDefined(b, FIELD_CANDIDATES.timestamp) || 0).getTime();
          return bTime - aTime;
        });

        console.log("merged response.data.values:", mergedHistoryData);

        const normalizedRows = mergedHistoryData.map((row, index) => normalizeHistoryRow(row, index));

        console.log("final table rows:", normalizedRows);

        if (!isActive) {
          return;
        }

        setHistoryRows(normalizedRows);
        const entitlementTotalCount = Number(entitlementResponse?.data?.totalCount) || entitlementRows.length || 0;
        const overrideTotalCount = Number(overrideHistoryResponse?.data?.totalCount) || overrideRows.length || 0;
        const combinedTotalCount = entitlementTotalCount + overrideTotalCount;

        const entitlementTotalPages = Number(entitlementResponse?.data?.totalPages) || 0;
        const overrideTotalPages = Number(overrideHistoryResponse?.data?.totalPages) || 0;

        setTotalCount(combinedTotalCount || mergedHistoryData.length || 0);
        setTotalPages(Math.max(entitlementTotalPages, overrideTotalPages));

        const apiTotalPages = Math.max(entitlementTotalPages, overrideTotalPages);
        const apiTotalCount = combinedTotalCount || mergedHistoryData.length || 0;

        setRowsPerPage((prevRowsPerPage) =>
          apiTotalPages > 0
            ? Math.max(1, Math.ceil(apiTotalCount / apiTotalPages))
            : Math.max(1, mergedHistoryData.length || prevRowsPerPage)
        );
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error("Failed to load entitlement history", error);
        setError(error?.message || "Failed to load entitlement history");
        setHistoryRows([]);
        setTotalCount(0);
        setTotalPages(0);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      isActive = false;
    };
  }, [effectiveCompanyId, page]);

  useEffect(() => {
    setPage(0);
  }, [search, historyType, startDate, endDate, dateFilterOption]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages - 1) {
      setPage(Math.max(totalPages - 1, 0));
    }
  }, [page, totalPages]);

  const filteredData = useMemo(() => {
    let rows = [...historyRows];

    if (normalizedSearch) {
      const value = normalizedSearch;
      rows = rows.filter((row) => row.__searchText.includes(value));
    }

    if (historyType !== "ALL") {
      rows = rows.filter((row) => row.__historyBucket === historyType || row.__typeText.toUpperCase() === historyType);
    }

    if (startDate) {
      rows = rows.filter((row) => {
        if (!row.__timestamp) {
          return true;
        }

        return new Date(row.__timestamp) >= new Date(startDate);
      });
    }

    if (endDate) {
      rows = rows.filter((row) => {
        if (!row.__timestamp) {
          return true;
        }

        return new Date(row.__timestamp) <= new Date(`${endDate} 23:59:59`);
      });
    }

    return rows;
  }, [historyRows, normalizedSearch, historyType, startDate, endDate]);

  useEffect(() => {
    console.log("final table rows:", filteredData);
  }, [filteredData]);

  const resetFilters = () => {
    setSearch("");
    setHistoryType("ALL");
    setStartDate("");
    setEndDate("");
    setDateFilterOption("ALL");
    setCalendarAnchor(null);
    setFilterAnchor(null);
    setPage(0);
  };

  const toggleRowExpanded = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const handleDateFilterSelect = (option) => {
    setDateFilterOption(option);

    if (option !== "CUSTOM") {
      const { start, end } = calculateDateRange(option);
      setStartDate(start);
      setEndDate(end);
    }

    setCalendarAnchor(null);
  };

  const renderChange = (row) => {
    const rowKey = row.__rowKey;
    const entitlementChanges = getEntitlementChanges(row);
    const overrideChanges = getOverrideChanges(row);
    const effectiveChanges = entitlementChanges.length > 0 ? entitlementChanges : overrideChanges;
    const isExpanded = Boolean(expandedRows[rowKey]);
    const hasActiveSearch = search.trim().length > 0;

    if (effectiveChanges.length > 0) {
      const visibleChanges = hasActiveSearch
        ? effectiveChanges
        : isExpanded
        ? effectiveChanges
        : effectiveChanges.slice(0, 15);
      const hiddenCount = Math.max(effectiveChanges.length - visibleChanges.length, 0);

      return (
        <Box>
          {visibleChanges.map((change) => (
            <Typography variant="body2" key={`${rowKey}-${change.key}`}>
              {renderHighlightedText(`${change.key}: `, search)}
              <Box
                component="span"
                sx={{ color: isNotSetValue(change.oldValue) ? "text.disabled" : "inherit" }}
              >
                {renderHighlightedText(getChangeValueText(change.oldValue), search)}
              </Box>
              {renderHighlightedText(" → ", search)}
              <Box
                component="span"
                sx={{ color: isNotSetValue(change.newValue) ? "text.disabled" : "inherit" }}
              >
                {renderHighlightedText(getChangeValueText(change.newValue), search)}
              </Box>
            </Typography>
          ))}

          {hiddenCount > 0 && !hasActiveSearch && (
            <Button
              size="small"
              onClick={() => toggleRowExpanded(rowKey)}
              sx={{ mt: 0.5, p: 0, minWidth: "auto", textTransform: "none", fontWeight: 600 }}
            >
              {`+${hiddenCount} more`}
            </Button>
          )}

          {isExpanded && effectiveChanges.length > 15 && !hasActiveSearch && (
            <Button
              size="small"
              onClick={() => toggleRowExpanded(rowKey)}
              sx={{ mt: 0.5, ml: 1, p: 0, minWidth: "auto", textTransform: "none" }}
            >
              Show less
            </Button>
          )}
        </Box>
      );
    }

    const action = row.__actionText.toUpperCase();
    const subject = row.__subjectText;
    const summary = row.__summary;

    if (summary === "-") {
      return "-";
    }

    if (action.match(/ADD|CREATE|INSERT/)) {
      return (
        <>
          <Typography fontWeight={600}>{renderHighlightedText(`Added: ${subject}`, search)}</Typography>
          <Typography variant="body2">{renderHighlightedText(summary, search)}</Typography>
        </>
      );
    }

    if (action.match(/UPDATE|EDIT|MODIFY|CHANGE/)) {
      return (
        <>
          <Typography fontWeight={600}>{renderHighlightedText(`Updated: ${subject}`, search)}</Typography>
          <Typography variant="body2">{renderHighlightedText(summary, search)}</Typography>
        </>
      );
    }

    if (action.match(/REMOVE|DELETE|ARCHIVE/)) {
      return (
        <>
          <Typography fontWeight={600}>{renderHighlightedText(`Removed: ${subject}`, search)}</Typography>
          <Typography variant="body2">{renderHighlightedText(summary, search)}</Typography>
        </>
      );
    }

    return <Typography variant="body2">{renderHighlightedText(summary, search)}</Typography>;
  };

  const visibleRows = filteredData;
  const paginationCount = search || historyType !== "ALL" || startDate || endDate ? visibleRows.length : totalCount;
  const emptyMessage = error || "No entitlement history found";

  return (
    <Box sx={{ p: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 1 }}>
            Back
          </Button>

          <Typography variant="h4" fontWeight={700}>
            Audit History
          </Typography>

          <Typography color="text.secondary">{stateCompanyName || "Company"}</Typography>
          <Typography color="text.secondary">Company ID: {effectiveCompanyId || "-"}</Typography>
        </Box>
      </Box>

      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={(e) => setCalendarAnchor(e.currentTarget)}
            sx={{
              minWidth: "auto",
              p: 1,
              color: T.teal,
              border: `1px solid ${T.border}`,
              borderRadius: "8px",
              "&:hover": {
                bgcolor: T.tealExtraLight,
              },
            }}
          >
            <CalendarTodayIcon />
          </Button>

          <Menu anchorEl={calendarAnchor} open={Boolean(calendarAnchor)} onClose={() => setCalendarAnchor(null)}>
            <MenuItem onClick={() => handleDateFilterSelect("ALL")} selected={dateFilterOption === "ALL"}>
              All Time
            </MenuItem>

            <MenuItem onClick={() => handleDateFilterSelect("LAST_MODIFICATION")} selected={dateFilterOption === "LAST_MODIFICATION"}>
              Last 7 Days
            </MenuItem>

            <MenuItem onClick={() => handleDateFilterSelect("THIS_MONTH")} selected={dateFilterOption === "THIS_MONTH"}>
              This Month
            </MenuItem>

            <MenuItem onClick={() => handleDateFilterSelect("THIS_YEAR")} selected={dateFilterOption === "THIS_YEAR"}>
              This Year
            </MenuItem>

            <MenuItem onClick={() => handleDateFilterSelect("LAST_YEAR")} selected={dateFilterOption === "LAST_YEAR"}>
              Last Year
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={() => {
                setDateFilterOption("CUSTOM");
                setCalendarAnchor(null);
              }}
              selected={dateFilterOption === "CUSTOM"}
            >
              Custom Range
            </MenuItem>
          </Menu>

          <Typography variant="body2" sx={{ minWidth: "120px", color: "text.secondary" }}>
            {dateFilterOption === "ALL"
              ? "All Time"
              : dateFilterOption === "LAST_MODIFICATION"
              ? "Last 7 Days"
              : dateFilterOption === "THIS_MONTH"
              ? "This Month"
              : dateFilterOption === "THIS_YEAR"
              ? "This Year"
              : dateFilterOption === "LAST_YEAR"
              ? "Last Year"
              : "Custom"}
          </Typography>
        </Box>

        {dateFilterOption === "CUSTOM" && (
          <>
            <TextField
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{ width: "150px" }}
            />

            <Typography variant="body2">to</Typography>

            <TextField
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{ width: "150px" }}
            />
          </>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          sx={{
            minWidth: "auto",
            p: 1,
            color: T.teal,
            border: `1px solid ${T.border}`,
            borderRadius: "8px",
            "&:hover": {
              bgcolor: T.tealExtraLight,
            },
          }}
        >
          <FilterListIcon />
        </Button>

        <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)}>
          <MenuItem
            onClick={() => {
              setHistoryType("ALL");
              setFilterAnchor(null);
            }}
            selected={historyType === "ALL"}
          >
            All Types
          </MenuItem>

          <MenuItem
            onClick={() => {
              setHistoryType("ENTITLEMENT");
              setFilterAnchor(null);
            }}
            selected={historyType === "ENTITLEMENT"}
          >
            Entitlement History
          </MenuItem>

          <MenuItem
            onClick={() => {
              setHistoryType("OVERRIDE");
              setFilterAnchor(null);
            }}
            selected={historyType === "OVERRIDE"}
          >
            Override History
          </MenuItem>
        </Menu>

        <Typography variant="body2" sx={{ minWidth: "100px", color: "text.secondary" }}>
          {historyType === "ALL"
            ? "All Types"
            : historyType === "ENTITLEMENT"
            ? "Entitlements"
            : "Overrides"}
        </Typography>

        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "20px" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "250px",
            "& .MuiOutlinedInput-root": {
              height: "40px",
            },
          }}
        />

        <Button
          onClick={resetFilters}
          size="small"
          sx={{
            minWidth: "auto",
            p: 1,
            color: T.teal,
            border: `1px solid ${T.border}`,
            borderRadius: "8px",
            "&:hover": {
              bgcolor: T.tealExtraLight,
            },
          }}
        >
          Reset
        </Button>
      </Paper>

      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Updated</TableCell>
                    <TableCell>Modified By</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Changes</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows.map((row, index) => (
                      <TableRow key={row.__rowKey || getRowKey(row, index)}>
                        <TableCell>{renderHighlightedText(row.__timestampText, search)}</TableCell>
                        <TableCell>{renderHighlightedText(row.__actorText, search)}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.__historyBucket}
                            size="small"
                            color={getChipColor(row.__historyBucket)}
                            variant={row.__historyBucket === "UNKNOWN" ? "outlined" : "filled"}
                          />
                        </TableCell>
                        <TableCell>{renderChange(row)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={paginationCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(event, newPage) => {
                setPage(newPage);
              }}
              rowsPerPageOptions={[rowsPerPage]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}